// Helix 2000 - Platformer MMORPG
// Character Creation System - Fixed & Enhanced

// Main-game redirect MUST be the FIRST import — on helix2000.com, bare /
// redirects to /resume before any other module evaluates (no blank-page
// flash). Runs before applyModeUrlCoercion so the redirect target URL is
// canonical by the time coercion evaluates it.
import './config/boot/applyMainGameRedirect';
import { isMainGameDomain } from './config/environment/environment';
import { bootHref, bootPathname, bootSearchParams } from './config/boot/bootUrl';
import { navigateInGame } from './config/boot/inGameNavigation';
import { isDebugSurface } from './shared/diagnostics/isDebugSurface';

// Mode-URL coercion MUST run before any module that reads the URL at load
// time (devConfig.ts resolves the active preset at ITS module load).
// When this block lived in main.tsx's BODY (which runs after all imports),
// every same-tab navigation to /editor etc. booted with the pre-coercion
// URL → wrong preset → wrong scene, no editor overlay (masked on first
// visits by the one-time ?reset=1 wipe+reload).
import './config/boot/applyModeUrlCoercion';
import { isScreensaverMode } from './config/screensaver/isScreensaverMode';
import {
  applyClientResetCheck,
  getNativeConsoleInfo,
  CLIENT_RESET_VERSION,
} from './shared/constants/clientResetVersion';

// Boot-check — runs BEFORE any React mount or hook reads localStorage.
// On a CLIENT_RESET_VERSION bump every visitor wipes their helix_* cache
// once on next page load, then the new version sticks. See
// src/shared/constants/clientResetVersion.ts for the bump procedure.
//
// Also handles `?reset=1` — an explicit one-tab wipe (no constant bump), then
// a reload without the param (a game navigation: D44 keeps it on helix2000.com).
try {
  if (typeof localStorage !== 'undefined') {
    const log = getNativeConsoleInfo();

    let resetUrlOverride = false;
    try {
      resetUrlOverride = bootSearchParams().get('reset') === '1';
    } catch { /* parse error → treat as no override */ }

    if (resetUrlOverride) {
      // Force a wipe by deleting the version key first; applyClientResetCheck
      // then sees current === null and goes through the install path, but
      // it removes every helix_* key it finds along the way.
      localStorage.removeItem('helix_reset_version');
      const r = applyClientResetCheck(localStorage);
      if (log) {
        log(
          `[ClientReset] ?reset=1 forced wipe of ${r.removedKeys.length} keys (now "${r.toVersion}") — clearing Twitch session + reloading`,
        );
      }
      // Also clear the HttpOnly Twitch session cookie via the existing
      // logout endpoint. JS can't touch HttpOnly cookies directly; we need
      // the server to send `Set-Cookie: …; Max-Age=0`. Fire-and-forget with
      // a 1.5s timeout — if the server is slow/down, reload anyway so DJ
      // isn't stranded. AbortController + Promise.race gives us the cap.
      const reloadAndStripParam = () => {
        try {
          const url = new URL(bootHref());
          url.searchParams.delete('reset');
          navigateInGame(url.toString(), { replace: true });
        } catch { window.location.reload(); }
      };
      const ac = new AbortController();
      const timeout = setTimeout(() => ac.abort(), 1500);
      fetch('/api/auth/twitch/logout', {
        method: 'POST',
        credentials: 'same-origin',
        signal: ac.signal,
      })
        .catch(() => {/* network error / abort — reload anyway */})
        .finally(() => {
          clearTimeout(timeout);
          reloadAndStripParam();
        });
    } else {
      const r = applyClientResetCheck(localStorage);
      if (r.outcome === 'wiped' && log) {
        // First-time install ('installed') stays silent so it doesn't
        // pollute the console for every brand-new visitor. A real bump
        // logs so the wipe is visible in DevTools.
        log(
          `[ClientReset] wiped ${r.removedKeys.length} keys (was "${r.fromVersion}", now "${CLIENT_RESET_VERSION}")`,
        );
      }
    }
  }
} catch {
  // localStorage disabled / Safari private mode — no-op.
}

import { Game } from '@/game/Game';
import { WindowManagerProvider } from '@/game/ui/framework/WindowManagerContext';
import { WindowHost } from '@/game/ui/framework/WindowHost';
import { WindowLauncher } from '@/game/ui/framework/WindowLauncher';
import { PlaytestRigApplier } from '@/game/diagnostics/PlaytestRigApplier';
import { CastStripBridge } from '@/game/systems/netobs/CastStripBridge';
import { LatencyReporter } from '@/game/systems/netobs/LatencyReporter';
import { FrameJitterReporter } from '@/game/systems/netobs/FrameJitterReporter';
import { SnapshotStatsReporter } from '@/game/systems/netobs/SnapshotStatsReporter';
import { SendRateReporter } from '@/game/systems/netobs/SendRateReporter';
import { DiagSnapshotReporter } from '@/game/systems/netobs/DiagSnapshotReporter';
import { BrowserPerfReporter } from '@/game/systems/netobs/BrowserPerfReporter';
import { AllocSampleReporter } from '@/game/systems/netobs/AllocSampleReporter';
import { FreezeSentinel } from '@/game/systems/netobs/FreezeSentinel';
import { DevStatusBarReporter } from '@/game/systems/netobs/DevStatusBarReporter';
import { PhysicsTraceReporter } from '@/game/systems/netobs/PhysicsTraceReporter';
import { NetTraceReporter } from '@/game/systems/netobs/NetTraceReporter';
import { NetobsBroadcastPublisher } from '@/game/systems/netobs/popout/NetobsBroadcastPublisher';
import { isPopoutMode } from '@/game/systems/netobs/popout/popoutMode';
import { NetobsPopoutView } from '@/game/ui/NetobsPopoutView';
import { SimLiveHUD } from '@/game/ui/HUD/SimLiveHUD';
import { FollowLeaderController } from '@/game/systems/spectator/FollowLeaderController';
import { RemotePlayerVfxBridge } from '@/game/systems/spectator/RemotePlayerVfxBridge';
import { KillCamBridge } from '@/game/systems/spectator/KillCamBridge';
import { StatusEffectBridge } from '@/game/systems/spectator/StatusEffectBridge';
import { DeathOverlay } from '@/game/ui/HUD/DeathOverlay';
import { SocialOverlays } from '@/game/ui/HUD/SocialOverlays';
import { DrainOverlay } from '@/game/ui/HUD/DrainOverlay';
import { LobbyInfoPanel } from '@/game/ui/HUD/LobbyInfoPanel';
import { SurfacePanels } from '@/game/ui/panels/SurfacePanels';
import { LagAttributionOverlay } from '@/game/ui/panels/LagAttributionOverlay';
import { MultiplayerLabelsReporter } from '@/game/ui/netobs/MultiplayerLabelsReporter';
import { KillFeedPanel } from '@/game/ui/HUD/KillFeedPanel';
import { ColdCloneBanner } from '@/game/ui/components/ColdCloneBanner';
import { ClassSwitcherTopBar } from '@/game/ui/HUD/ClassSwitcherTopBar';
import { HomeModePicker, shouldShowHomeModePicker } from '@/game/ui/HUD/HomeModePicker';
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ConceptArtGallery } from '@/components/ConceptArtGallery';
import { GameProvider } from '@/game/ui';
import { ChatProvider } from '@/game/ui/contexts/ChatContext';
import { InteractionProvider } from '@/game/ui/contexts/InteractionContext';
import { CharacterCustomizer } from '@/game/ui/Menus/CharacterCustomizer';
import { getGameTitle, VERSION_INFO } from './config/version/version';
import { ServiceRegistry } from './game/core/ServiceRegistry';
import { MapSystem } from './game/systems/map/MapSystem';
import { LevelService } from './game/services/LevelService';
import { DevConfig } from './config/environment/devConfig';

import { logDeviceInfo } from './utils/deviceDetection';
import { gameLogger } from './utils/GameLogger';
import { generatePigSVG, generateRandomPig, PIG_PRESETS } from './utils/NPCGenerator';

import { LandingShell } from './game/ui/Portal/LandingShell';
import { RotateDeviceNotice } from '@/game/ui/overlays/RotateDeviceNotice';

// Phase H-B — under ?renderer=pixi, register the Pixi factories so the
// dispatch helpers in BarLabel.ts / InteractableVisual.ts can return
// Pixi-backed impls. The dynamic imports keep pixi.js out of the
// bundle's eager path (and out of jsdom test env, which can't load
// pixi's browser-only utilities).
import { getRendererMode } from './game/rendering/RendererFactory';
import { registerPixiBarLabelFactory } from './game/rendering/utils/BarLabel';
import { registerPixiInteractableFactory } from './game/rendering/utils/InteractableVisual';
import { registerPixiNameTagFactory } from './game/rendering/utils/NameTag';
import { registerPixiGraphicsFactory } from './game/rendering/utils/Graphics';
import { registerPixiSpriteFactory } from './game/rendering/utils/Sprite';
import { registerPixiDebugLabelFactory } from './game/rendering/utils/DebugLabel';
import { registerPixiParticleEmitterFactory } from './game/rendering/utils/ParticleEmitter';
import { registerPixiPlatformVisualFactory } from './game/rendering/utils/PlatformVisual';
if (getRendererMode() === 'pixi') {
  void import('./game/rendering/PixiBarLabel').then(({ PixiBarLabel }) => {
    registerPixiBarLabelFactory((x, y) => new PixiBarLabel(x, y));
    gameLogger.info('renderer', '🎨 Pixi BarLabel factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiInteractableVisual').then(({ PixiInteractableVisual }) => {
    registerPixiInteractableFactory((opts) => new PixiInteractableVisual(opts));
    gameLogger.info('renderer', '🎨 Pixi InteractableVisual factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiNameTag').then(({ PixiNameTag }) => {
    registerPixiNameTagFactory((x, y, t, style) => new PixiNameTag(x, y, t, style));
    gameLogger.info('renderer', '🎨 Pixi NameTag factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiGraphics').then(({ PixiGraphics }) => {
    registerPixiGraphicsFactory((style) => new PixiGraphics(style));
    gameLogger.info('renderer', '🎨 Pixi Graphics factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiSprite').then(({ PixiSprite }) => {
    registerPixiSpriteFactory((opts) => new PixiSprite(opts));
    gameLogger.info('renderer', '🎨 Pixi Sprite factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiDebugLabel').then(({ PixiDebugLabel }) => {
    registerPixiDebugLabelFactory(() => new PixiDebugLabel());
    gameLogger.info('renderer', '🎨 Pixi DebugLabel factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiParticleEmitter').then(({ PixiParticleEmitter }) => {
    registerPixiParticleEmitterFactory((cfg) => new PixiParticleEmitter(cfg));
    gameLogger.info('renderer', '🎨 Pixi ParticleEmitter factory registered (?renderer=pixi)');
  });
  void import('./game/rendering/PixiPlatformVisual').then(({ PixiPlatformVisual }) => {
    registerPixiPlatformVisualFactory((opts) => new PixiPlatformVisual(opts));
    gameLogger.info('renderer', '🎨 Pixi PlatformVisual factory registered (?renderer=pixi)');
  });
}

// Lazy-load dev tools (not needed on initial page load)
const DiagnosticHub = React.lazy(() => import('./game/ui/DiagnosticHub/DiagnosticHub').then(m => ({ default: m.DiagnosticHub })));
const PretestLoader = React.lazy(() => import('./game/ui/PretestLoader/PretestLoader').then(m => ({ default: m.PretestLoader })));

// Import CSS
import './client/styles/index.css';
import './client/styles/maplestory.css';
import './client/styles/splash.css';

// NPC Creator View Component
const NPCCreatorView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [seed, setSeed] = useState(Date.now());

  const presetNames = Object.keys(PIG_PRESETS);
  const randomPigs = Array.from({ length: 6 }, (_, i) => {
    const id = `preview_${seed}_${i}`;
    return { id, props: generateRandomPig(id) };
  });

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: 'linear-gradient(135deg, #2d1f4f 0%, #432e6e 50%, #5c3d8f 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      overflow: 'auto'
    }}>
      <h1 style={{ color: 'white', marginBottom: '10px', textShadow: '0 0 10px rgba(255,100,200,0.5)' }}>
        🐷 NPC Creator 🐷
      </h1>
      <p style={{ color: '#ddd', marginBottom: '20px' }}>
        MapleStory-style procedural pig variations with accessories, expressions, and markings!
      </p>

      {/* Preset Pigs */}
      <h2 style={{ color: '#f5a', margin: '10px 0' }}>Named Presets</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '15px',
        padding: '15px',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '15px',
        marginBottom: '20px'
      }}>
        {presetNames.map(name => {
          const baseProps = generateRandomPig(name);
          const preset = PIG_PRESETS[name];
          const mergedProps = { ...baseProps, ...preset };
          const svg = generatePigSVG(mergedProps);

          return (
            <div key={name} style={{ textAlign: 'center' }}>
              <div
                dangerouslySetInnerHTML={{ __html: svg }}
                style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px' }}
              />
              <div style={{ color: 'white', fontSize: '12px', marginTop: '5px' }}>{name}</div>
            </div>
          );
        })}
      </div>

      {/* Angle Rotations Demo */}
      <h2 style={{ color: '#5af', margin: '10px 0' }}>Directional Views (0° → 90°)</h2>
      <p style={{ color: '#aaa', fontSize: '12px', marginBottom: '10px' }}>
        Mirror for opposite direction when moving
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        padding: '15px',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '15px',
        marginBottom: '20px'
      }}>
        {['Wild Pig', 'Ribbon Pig', 'King Pig', 'Angry Pig'].map(presetName => {
          const baseProps = generateRandomPig(presetName);
          const preset = PIG_PRESETS[presetName];
          const mergedProps = { ...baseProps, ...preset };

          return (
            <div key={presetName}>
              <div style={{ color: 'white', fontSize: '11px', marginBottom: '5px', textAlign: 'center' }}>{presetName}</div>
              <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                {([0, 30, 60, 90] as const).map(angle => (
                  <div key={angle} style={{ textAlign: 'center' }}>
                    <div
                      dangerouslySetInnerHTML={{ __html: generatePigSVG(mergedProps as any, 50) }}
                      style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '5px', padding: '3px' }}
                    />
                    <div style={{ color: '#888', fontSize: '9px' }}>{angle}°</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Random Pigs */}
      <h2 style={{ color: '#5af', margin: '10px 0' }}>Random Variations</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '15px',
        padding: '15px',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '15px',
        marginBottom: '20px'
      }}>
        {randomPigs.map(({ id, props }) => {
          const svg = generatePigSVG(props);
          return (
            <div key={id} style={{ textAlign: 'center' }}>
              <div
                dangerouslySetInnerHTML={{ __html: svg }}
                style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px' }}
              />
              <div style={{ color: '#aaa', fontSize: '10px', marginTop: '5px' }}>
                {props.expression} | {props.accessory?.type || 'no accessory'} | {props.markings || 'none'}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        <button
          onClick={() => setSeed(Date.now())}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          🎲 Randomize
        </button>
        <button
          onClick={onBack}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          ← Back
        </button>
      </div>
    </div>
  );
};

/**
 * Route guard for /pretest. The PretestLoader is a dev/QA scenario picker, NOT
 * a user-facing entry. To open it you must explicitly opt in — otherwise a
 * stray click / bookmark / cached URL lands on the game instead of a wall of
 * checkboxes.
 *
 * Opt-in (any one):
 *   - URL:            /pretest?dev=1  (or any ?pretest=1)
 *   - localStorage:   helix_use_pretest = '1'
 */
const PretestGate: React.FC = () => {
  let allow = false;
  try {
    const sp = bootSearchParams();
    allow = sp.get('dev') === '1' || sp.get('pretest') === '1';
    if (!allow) {
      allow = localStorage.getItem('helix_use_pretest') === '1';
    }
  } catch { /* safari private mode etc. */ }

  if (!allow) {
    return <Navigate to="/" replace />;
  }
  return (
    <React.Suspense fallback={<div style={{ color: 'white', padding: '20px' }}>Loading pretest loader...</div>}>
      <PretestLoader onBack={() => window.history.back()} />
    </React.Suspense>
  );
};

// Main App Component (Game + Splash)
const App: React.FC = () => {
  // Popout gate — `?popout=1` renders only the netobs panels (no
  // game canvas, no Colyseus connection). Mirror of the parent
  // window's data via BroadcastChannel. See
  // src/game/ui/NetobsPopoutView.tsx + popout/popoutChannel.ts.
  const popout = isPopoutMode();

  // HomeModePicker gate — `shouldShowHomeModePicker()` returns true
  // only on bare `/` with no functional params. Stable for the
  // mount session (URL doesn't mutate without a navigation, which
  // unmounts App), so reading it once on first render is safe.
  // Hooks must run BEFORE any conditional return — `useState`/
  // `useEffect` below would otherwise be called inconsistently
  // across renders.
  const showHomePicker = shouldShowHomeModePicker();

  const [currentView, setCurrentView] = useState<'splash' | 'game' | 'gallery' | 'character_creator' | 'npc_creator'>(
    DevConfig.skipSplash ? 'game' : 'splash'
  );

  // Log device info on mount
  React.useEffect(() => {
    logDeviceInfo();
  }, []);

  if (popout) {
    return <NetobsPopoutView />;
  }

  if (showHomePicker) {
    return <HomeModePicker />;
  }

  const handleStartGame = () => {
    gameLogger.info('boot', `🚀 Starting ${getGameTitle()}...`);
    setCurrentView('game');
  };

  const handleOpenGallery = () => {
    gameLogger.info('boot', '🎨 Opening Gallery...');
    setCurrentView('gallery');
  };

  const handleOpenCharacterCreator = () => {
    gameLogger.info('boot', '✨ Opening Character Creator...');
    setCurrentView('character_creator');
  };

  const handleOpenNPCCreator = () => {
    gameLogger.info('boot', '🐷 Opening NPC Creator...');
    setCurrentView('npc_creator');
  };

  const handleExitGame = () => {
    gameLogger.info('boot', '👋 Returning to splash screen...');
    setCurrentView('splash');
  };

  if (currentView === 'game') {
    return (
      <WindowManagerProvider>
        {/* Phone-landscape notice only when actively playing the game, leaving mode picker and web menus accessible */}
        <RotateDeviceNotice />
        <Game onExit={handleExitGame} />
        <SurfacePanels />
        <LagAttributionOverlay />
        <WindowHost />
        <WindowLauncher />
        <PlaytestRigApplier />
        <CastStripBridge />
        <BrowserPerfReporter />
        <AllocSampleReporter />
        {/* Always-on (NOT netobs-gated): catches unpredictable freezes in normal play. */}
        <FreezeSentinel />
        <DevStatusBarReporter />
        <LatencyReporter />
        <NetTraceReporter />
        <PhysicsTraceReporter />
        <FrameJitterReporter />
        <SnapshotStatsReporter />
        <SendRateReporter />
        <DiagSnapshotReporter />
        <MultiplayerLabelsReporter />
        <NetobsBroadcastPublisher />
        <SimLiveHUD />
        <FollowLeaderController />
        <RemotePlayerVfxBridge />
        <KillCamBridge />
        <StatusEffectBridge />
        {/* Screensaver = Alt+Z: hide all top-level UI chrome (the bar, kill feed,
            class switcher, banners). Functional bridges above stay mounted. */}
        {!isScreensaverMode() && (
          <>
            <DeathOverlay />
            <SocialOverlays />
            <DrainOverlay />
            <LobbyInfoPanel />
            <KillFeedPanel />
            <ClassSwitcherTopBar />
            <ColdCloneBanner />
          </>
        )}
      </WindowManagerProvider>
    );
  }

  if (currentView === 'gallery') {
    return <ConceptArtGallery onBack={handleExitGame} />;
  }

  if (currentView === 'character_creator') {
    const handleCreateCharacter = async (name: string, charClass: string, appearance: any) => {
      gameLogger.success('character', '✅ Character Created', { name, charClass, appearance });
      localStorage.setItem('helix_new_character_appearance', JSON.stringify({ name, charClass, appearance }));
      setCurrentView('splash');
    };

    return (
      <CharacterCustomizer
        onCreate={handleCreateCharacter}
        onCancel={handleExitGame}
      />
    );
  }

  if (currentView === 'npc_creator') {
    return <NPCCreatorView onBack={handleExitGame} />;
  }

  return (
    <LandingShell
      onStartGame={handleStartGame}
      onOpenCharacterCreator={handleOpenCharacterCreator}
      onOpenNPCCreator={handleOpenNPCCreator}
      onOpenGallery={handleOpenGallery}
    />
  );
};

// VQA arc dev hook — only installs when ?vqa=1 is set. See
// docs/plans/2026-04-17-VQA-ARC-PLAN.md §Unknowns #4.
import { installVqaHook } from './game/vqa/__vqa_hook';
installVqaHook();

// Render-truth introspection for 2-client parity tests — only installs when
// ?testhooks=1 (or ?vqa=1). See docs/testing/TEST_ENVIRONMENTS.md + tests/2client/.
import { installRenderStateHook } from './game/testhooks/__h2k_render_state';
installRenderStateHook();

// Phase 1 state seam — typed game-state snapshot for automation scenarios.
// Same ?testhooks=1 (or ?vqa=1) gate as the render-state hook above.
// Blueprint: docs/automation/PHASE1_STATE_SEAM_BLUEPRINT.md
import { installGameWorldViewHook } from './game/diagnostics/GameWorldView';
installGameWorldViewHook();

// Editor test hooks — exposes LevelFormatConverter + analyzeReachability
// for MCP-driven editor testing tools (reachability analysis from editor).
// Same ?testhooks=1 (or ?vqa=1) gate as the hooks above.
import { installEditorTestHooks } from './game/testhooks/__editor_test_hooks';
installEditorTestHooks();

// Auto-screenshot-on-cast diagnostics — only installs when ?abilityCapture=1
// (auto-injected on /abilitygrounds). Frames upload to the connected game
// server: /api/diag/ability-captures (list/pull/drain; 60-min retention).
import { installAbilityCastCapture } from './game/diagnostics/AbilityCastCapture';
installAbilityCastCapture();

// Phase C.1 (2026-05-12) — DamageSyncSystem forwards the server's two
// damage broadcasts into a unified EVENTS.DAMAGE_APPLIED stream so
// downstream consumers (CombatManager, DamagePresentationService,
// CombatLogService) subscribe once with a typed payload instead of
// independently casting two raw broadcasts to `any`. Idempotent +
// fire-and-forget — no teardown needed at this scope.
import { DamageSyncSystem } from './game/systems/combat/DamageSyncSystem';
DamageSyncSystem.start();

// Initialize the app
const container = document.getElementById('root');
if (!container) {
  gameLogger.error('boot', '❌ Root element not found!');
  document.body.innerHTML = '❌ Root element not found!';
} else {
  gameLogger.info('boot', `🎮 Initializing ${getGameTitle()}...`);
  gameLogger.info('boot', `📋 Version: ${VERSION_INFO.fullVersion}`);
  gameLogger.info('boot', `🔧 Environment: ${VERSION_INFO.environment}`);

  // Global error handlers — log + show in-game toast + fatal overlay
  // In dev mode, suppress the fatal overlay and toast for HMR-related transient errors
  // (Vite reloads, React re-render churn, etc.) to keep the screen clean.
  // The fatal overlay still surfaces on debug surfaces (?debug=1 or /debug) so QA
  // can capture full client errors without shipping a production build.
  const isDev = import.meta.env.DEV;
  const isHmrTransientError = (msg: any): boolean => {
    const s = String(msg || '');
    return (
      s.includes('Could not Fast Refresh') ||
      s.includes('Failed to fetch dynamically imported module') ||
      s.includes('Importing a module script failed') ||
      s.includes('HMR') ||
      s.includes('hmr') ||
      s.includes('vite') ||
      s.includes('Loading chunk')
    );
  };

  // Render the full-screen fatal overlay. Deduplicated by element id; guard is on callers.
  const showFatalOverlay = (title: string, head: string, stack: string, source?: string) => {
    const div = document.createElement('div');
    div.id = 'fatal-error-overlay';
    div.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(20,0,0,0.9);color:#ff5555;z-index:999999;padding:2rem;font-family:monospace;overflow:auto;pointer-events:auto;';
    div.innerHTML = `
      <h1>💥 ${title} 💥</h1>
      <h2>${head}</h2>
      ${source ? `<p>Source: ${source}</p>` : ''}
      <pre style="white-space:pre-wrap;color:#aaa">${stack || 'No stack trace'}</pre>
      <button onclick="window.location.reload()" style="margin-top:20px;padding:10px 20px;background:#ff5555;color:#fff;border:none;cursor:pointer;border-radius:4px;">Reload Game</button>
    `;
    document.body.appendChild(div);
  };

  window.onerror = (message, source, lineno, colno, error) => {
    // Skip HMR / Vite transient errors entirely in dev
    if (isDev && (isHmrTransientError(message) || isHmrTransientError(source))) {
      return;
    }

    gameLogger.error('boot', '🔥 [GLOBAL ERROR]', { message, source, lineno, colno, error });

    // Push to in-game ErrorToast via globalThis callback (set when toast mounts)
    try {
      (globalThis as any).__errorToast?.('error', `${String(message).slice(0, 120)}`);
    } catch { /* toast not mounted yet */ }

    // Fatal overlay for black-screen crashes — DEV: suppress (we have HMR), PROD: show
    // Debug surfaces (?debug=1, /debug, Playwright) also show it so full error context is visible.
    if ((!isDev || isDebugSurface()) && !document.getElementById('fatal-error-overlay')) {
      showFatalOverlay(
        'FATAL BROWSER CRASH',
        String(message),
        error?.stack || 'No stack trace',
        `${source}:${lineno}:${colno}`,
      );
    }
  };
  window.onunhandledrejection = (event) => {
    if (isDev && isHmrTransientError(event.reason)) return;
    gameLogger.error('boot', '🔥 [UNHANDLED REJECTION]', { reason: event.reason });
    try {
      (globalThis as any).__errorToast?.('error', `Rejection: ${String(event.reason).slice(0, 100)}`);
    } catch { /* toast not mounted yet */ }

    // Promises that reject without a handler can also black-screen the game; surface them
    // in production and on debug surfaces the same way as synchronous global errors.
    if ((!isDev || isDebugSurface()) && !document.getElementById('fatal-error-overlay')) {
      showFatalOverlay(
        'FATAL UNHANDLED REJECTION',
        `Rejection: ${String(event.reason).slice(0, 120)}`,
        event.reason?.stack || 'No stack trace',
      );
    }
  };

  // Register core services early
  const registry = ServiceRegistry.getInstance();
  if (!registry.has('MapSystem')) {
    registry.register('MapSystem', new MapSystem(LevelService.getInstance()));
  }

  // Use dynamic basename detection to handle / or /helix2000/ gracefully
  const basename = bootPathname().startsWith('/helix2000') ? '/helix2000/' : '/';

  const root = createRoot(container);
  root.render(
    <GlobalErrorBoundary>
      <GameProvider>
        <ChatProvider>
          <InteractionProvider>
            <BrowserRouter basename={basename}>
              <Routes>
                <Route path="/" element={<App />} />
                <Route path="/login" element={<App />} />
                {/* /diagnostic — DiagnosticHub. Gated off on the main game domain
                    (helix2000.com): every /api/diag/* 404s there (ADMIN_ENDPOINTS_ENABLED
                    unset), so the page would render empty. Redirect prod players to /.
                    PTR + localhost keep the route. */}
                <Route path="/diagnostic" element={isMainGameDomain() ? <App /> : <React.Suspense fallback={<div style={{ color: 'white', padding: '20px' }}>Loading diagnostics...</div>}><DiagnosticHub /></React.Suspense>} />
                <Route path="/pretest" element={<PretestGate />} />
                <Route path="/register" element={<App />} />
                <Route path="/auth" element={<App />} />
                {/* /editor — one-click entry into the in-game level editor.
                    Mirrors the /brawl + /arena idiom: renders <App/> so the
                    module-load coerceModeUrl({ path: '/editor', … }) call above
                    rewrites the params (map=empty-canvas + preset=editor +
                    editorOn=1 + skip screens) per EDITOR_CONFIG. `?preset=editor`
                    flips DevConfig.systems.editor on; `?editorOn=1` flips
                    editorOverlayOnByDefault so the EditorOverlay mounts on boot. */}
                <Route path="/editor" element={<App />} />
                <Route path="/brawl" element={<App />} />
                <Route path="/arena" element={<App />} />
                <Route path="/pve" element={<App />} />
                <Route path="/adventure" element={<App />} />
                <Route path="/resume" element={<App />} />
                {/* /abilitytest rewritten to /abilitygrounds at module load; fallback route. */}
                <Route path="/abilitytest" element={<App />} />
                <Route path="/abilitygrounds" element={<App />} />
                <Route path="/lobby" element={<App />} />
                <Route path="/gmrace" element={<App />} />
                <Route path="/biomes" element={<App />} />
                <Route path="/debug" element={<App />} />
                <Route path="/combatlab" element={<App />} />
                <Route path="/qa/hollow-keeper" element={<App />} />
                <Route path="/mobile" element={<App />} />
                <Route path="*" element={<App />} />
              </Routes>
            </BrowserRouter>
          </InteractionProvider>
        </ChatProvider>
      </GameProvider>
    </GlobalErrorBoundary>
  );

  // Diagnostic toggle: ?hideui=1 hides the React root so you can test whether
  // the DOM/React overlay is responsible for compositor/rAF stalls. The Pixi
  // canvas (appended to <body>) remains visible and interactive.
  if (bootSearchParams().get('hideui') === '1') {
    container.style.display = 'none';
  }
}
